<template>
  <div class="row">
    <div class="col-md-12">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner class="align-middle"></b-spinner>
        <span class="ml-2">Loading NOC mappings...</span>
      </div>

      <template v-else>
        <!-- Summary Card -->
        <div class="card mb-3">
          <div class="card-header">
            <h4 class="card-title mb-0">Category → NOC Code Mappings</h4>
            <small class="text-muted">Maps job categories to NOC 2021 codes for AI matching</small>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ summary.total_categories }}</h3>
                  <small class="text-muted">Categories</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ summary.total_category_mappings }}</h3>
                  <small class="text-muted">NOC Mappings</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0">{{ summary.total_noc_codes_available }}</h3>
                  <small class="text-muted">Total NOC Codes</small>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-center">
                  <h3 class="mb-0" :class="{ 'text-danger': summary.invalid_category_nocs > 0, 'text-success': summary.invalid_category_nocs === 0 }">
                    {{ summary.invalid_category_nocs }}
                  </h3>
                  <small class="text-muted">Invalid Codes</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Categories List -->
        <div class="card">
          <div class="card-header">
            <b-form-input
              v-model="categorySearch"
              placeholder="Search categories..."
              class="mb-0"
            />
          </div>
          <div class="card-body">
            <div class="accordion" role="tablist">
              <b-card
                v-for="category in filteredCategories"
                :key="category.id"
                no-body
                class="mb-2"
              >
                <b-card-header header-tag="header" class="p-0" role="tab">
                  <b-button
                    block
                    v-b-toggle="`cat-${category.id}`"
                    variant="light"
                    class="text-left d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <strong>{{ category.label_en }}</strong>
                      <small class="text-muted ml-2">{{ category.label_fr }}</small>
                    </span>
                    <span>
                      <b-badge variant="primary" class="mr-1">{{ category.noc_count }} NOCs</b-badge>
                      <b-badge v-if="category.invalid_count > 0" variant="danger">
                        {{ category.invalid_count }} invalid
                      </b-badge>
                      <b-badge v-if="category.noc_count === 0" variant="warning">
                        No mappings
                      </b-badge>
                    </span>
                  </b-button>
                </b-card-header>
                <b-collapse :id="`cat-${category.id}`" accordion="category-accordion" role="tabpanel">
                  <b-card-body>
                    <div v-if="category.nocs.length === 0" class="text-muted">
                      No NOC codes mapped to this category.
                    </div>
                    <b-table
                      v-else
                      :items="category.nocs"
                      :fields="nocFields"
                      small
                      hover
                      class="mb-0"
                    >
                      <template #cell(noc_code)="{ item }">
                        <router-link
                          v-if="item.valid"
                          :to="{ name: 'noc-detail', params: { nocCode: item.noc_code } }"
                          class="noc-link"
                        >
                          <code>{{ item.noc_code }}</code>
                        </router-link>
                        <code v-else class="text-danger">{{ item.noc_code }}</code>
                      </template>
                      <template #cell(valid)="{ item }">
                        <i v-if="item.valid" class="fas fa-check text-success"></i>
                        <i v-else class="fas fa-times text-danger" title="NOC code not found in database"></i>
                      </template>
                      <template #cell(title_en)="{ item }">
                        <span v-if="item.title_en">{{ item.title_en }}</span>
                        <span v-else class="text-danger">Not found</span>
                      </template>
                      <template #cell(title_fr)="{ item }">
                        <span v-if="item.title_fr">{{ item.title_fr }}</span>
                        <span v-else class="text-danger">-</span>
                      </template>
                    </b-table>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>

            <!-- Issues Section -->
            <div v-if="totalIssues > 0" class="mt-4 pt-4 border-top">
              <h5 class="text-danger mb-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Issues ({{ totalIssues }})
              </h5>

              <!-- Invalid NOCs in Categories -->
              <div v-if="categoriesWithInvalidNocs.length > 0" class="mb-4">
                <h6 class="text-danger">Invalid NOC codes</h6>
                <ul class="list-group">
                  <li
                    v-for="cat in categoriesWithInvalidNocs"
                    :key="cat.id"
                    class="list-group-item"
                  >
                    <strong>{{ cat.label_en }}</strong>
                    <div class="mt-1">
                      <code
                        v-for="noc in cat.nocs.filter(n => !n.valid)"
                        :key="noc.noc_code"
                        class="text-danger mr-2"
                      >
                        {{ noc.noc_code }}
                      </code>
                    </div>
                  </li>
                </ul>
              </div>

              <!-- Categories without mappings -->
              <div v-if="categoriesWithoutMappings.length > 0">
                <h6 class="text-warning">Categories without mappings</h6>
                <ul class="list-group">
                  <li
                    v-for="cat in categoriesWithoutMappings"
                    :key="cat.id"
                    class="list-group-item"
                  >
                    {{ cat.label_en }}
                    <small class="text-muted ml-2">{{ cat.label_fr }}</small>
                  </li>
                </ul>
              </div>
            </div>

            <div v-else class="mt-4 pt-4 border-top text-center text-success">
              <i class="fas fa-check-circle fa-2x mb-2"></i>
              <p class="mb-0">All mappings are valid</p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BBadge,
  BFormInput,
  BCard,
  BCardHeader,
  BCardBody,
  BCollapse,
  BButton,
  BTable
} from "bootstrap-vue";

export default {
  components: {
    BSpinner,
    BBadge,
    BFormInput,
    BCard,
    BCardHeader,
    BCardBody,
    BCollapse,
    BButton,
    BTable
  },
  data() {
    return {
      isLoading: true,
      summary: {
        total_categories: 0,
        total_noc_codes_available: 0,
        total_category_mappings: 0,
        invalid_category_nocs: 0,
        categories_without_mappings: 0
      },
      categories: [],
      categorySearch: "",
      nocFields: [
        { key: "noc_code", label: "NOC Code", thStyle: { width: "100px" } },
        { key: "valid", label: "Valid", thStyle: { width: "60px" } },
        { key: "title_en", label: "Title (EN)" },
        { key: "title_fr", label: "Title (FR)" }
      ]
    };
  },
  async created() {
    await this.fetch();
    this.isLoading = false;
  },
  computed: {
    filteredCategories() {
      if (!this.categorySearch) return this.categories;
      const search = this.categorySearch.toLowerCase();
      return this.categories.filter(
        cat =>
          cat.label_en?.toLowerCase().includes(search) ||
          cat.label_fr?.toLowerCase().includes(search)
      );
    },
    categoriesWithInvalidNocs() {
      return this.categories.filter(cat => cat.invalid_count > 0);
    },
    categoriesWithoutMappings() {
      return this.categories.filter(cat => cat.noc_count === 0);
    },
    totalIssues() {
      return (
        (this.summary.invalid_category_nocs || 0) +
        (this.summary.categories_without_mappings || 0)
      );
    }
  },
  methods: {
    async fetch() {
      try {
        const { data } = await this.$http.get("/enroll_admin/noc-mappings");
        this.summary = data.summary;
        this.categories = data.categories;
      } catch (error) {
        this.$bvToast.toast("Failed to load NOC mappings", {
          title: "Error",
          variant: "danger",
          solid: true
        });
      }
    }
  }
};
</script>

<style scoped>
.card-title {
  font-size: 1rem;
  margin-bottom: 0;
}
.noc-link {
  text-decoration: none;
}
.noc-link:hover code {
  background-color: #e9ecef;
  text-decoration: underline;
}
</style>
